import React, { useMemo } from 'react';
import { View, Text, useWindowDimensions } from 'react-native';
import { PickCell } from './PickCell';
import { SoccerKit } from '../ui/SoccerKit';
import type { ChallengeCardStyles } from '../../src/challenges/styles/challengeCard.styles';
import { MATCH_WINNER_MARKET_NAME } from '../../src/challenges/constants/challenges.constants';
import type { TeamKitType } from '../../constants/teamKitColors';

export const WIDE_LAYOUT_MIN_WIDTH = 400;

export interface KitConfig {
  primary: string;
  secondary: string;
  type: TeamKitType;
}

export interface MatchBetPickSlotProps {
  label?: string;
  odds?: number;
  isCorrect?: boolean;
  isWrong?: boolean;
  onPress?: () => void;
  testID?: string;
}

export interface MatchBetThreeColumnGridProps {
  styles: ChallengeCardStyles;
  homeTeamName: string;
  awayTeamName: string;
  homeKit: KitConfig;
  awayKit: KitConfig;
  kitSize?: number;
  hasScore: boolean;
  homeScore?: number;
  awayScore?: number;
  matchDatePart?: string;
  matchTimePart?: string;
  myPick: MatchBetPickSlotProps;
  opponentPick: MatchBetPickSlotProps;
  /** Defaults to Match Winner 1X2 */
  centerMarketLabel?: string;
  /** Extra content below market name in center column (e.g. CorrectOutcomeRow on friend stats) */
  centerBetFooter?: React.ReactNode;
  /**
   * When false, only kits/names and center date/score are shown (no 1X2 pick row).
   * Use when the match has no offerable Match Winner market but other markets still render below.
   */
  showBetRow?: boolean;
  /** Shown under kickoff time when in play but not showing a final-style score block */
  liveCaption?: string | null;
  /** When true and hasScore is true, score is styled in live (red) color */
  isLive?: boolean;
}

export const MatchBetThreeColumnGrid: React.FC<MatchBetThreeColumnGridProps> = ({
  styles,
  homeTeamName,
  awayTeamName,
  homeKit,
  awayKit,
  kitSize = 28,
  hasScore,
  homeScore,
  awayScore,
  matchDatePart,
  matchTimePart,
  myPick,
  opponentPick,
  centerMarketLabel = MATCH_WINNER_MARKET_NAME,
  centerBetFooter,
  showBetRow = true,
  liveCaption,
  isLive = false,
}) => {
  const { width: screenWidth } = useWindowDimensions();
  const useWideLayout = screenWidth >= WIDE_LAYOUT_MIN_WIDTH;
  const pickCellContainerStyle = useWideLayout ? styles.pickCellWide : undefined;

  const colSideStyle = useMemo(
    () => [
      styles.matchBetCol,
      styles.matchBetColSide,
      useWideLayout && styles.matchBetColSideWide,
    ],
    [styles, useWideLayout]
  );
  const colCenterStyle = useMemo(
    () => [styles.matchBetCol, styles.matchBetColCenter],
    [styles]
  );

  return (
    <View style={styles.matchBetGrid}>
      <View style={colSideStyle}>
        <View style={styles.matchBetMatchCell}>
          <SoccerKit
            primary={homeKit.primary}
            secondary={homeKit.secondary}
            type={homeKit.type}
            size={kitSize}
          />
          <Text style={styles.matchTeamName} numberOfLines={2}>
            {homeTeamName}
          </Text>
        </View>
        {showBetRow ? (
          <View style={styles.matchBetBetCell}>
            <PickCell
              label={myPick.label}
              odds={myPick.odds}
              isCorrect={myPick.isCorrect}
              isWrong={myPick.isWrong ?? false}
              containerStyle={pickCellContainerStyle}
              onPress={myPick.onPress}
              testID={myPick.testID}
            />
          </View>
        ) : null}
      </View>
      <View style={colCenterStyle}>
        <View style={styles.matchBetMatchCell}>
          {hasScore ? (
            <Text style={[styles.matchScoreText, isLive && styles.matchScoreTextLive]}>
              {homeScore}:{awayScore}
            </Text>
          ) : (
            <>
              <Text style={styles.matchTeamsCenterDate} numberOfLines={1}>
                {matchDatePart || '–'}
              </Text>
              <Text style={styles.matchTeamsCenterTime} numberOfLines={1}>
                {matchTimePart || '–'}
              </Text>
              {liveCaption != null && liveCaption !== '' ? (
                <Text style={styles.matchTeamsCenterLiveCaption} numberOfLines={1}>
                  {liveCaption}
                </Text>
              ) : null}
            </>
          )}
        </View>
        {showBetRow ? (
          <View style={[styles.matchBetBetCell, styles.betRowMarketName]}>
            <Text style={styles.betRowMarketNameText} numberOfLines={2}>
              {centerMarketLabel}
            </Text>
            {centerBetFooter}
          </View>
        ) : null}
      </View>
      <View style={colSideStyle}>
        <View style={styles.matchBetMatchCell}>
          <SoccerKit
            primary={awayKit.primary}
            secondary={awayKit.secondary}
            type={awayKit.type}
            size={kitSize}
          />
          <Text style={styles.matchTeamName} numberOfLines={2}>
            {awayTeamName}
          </Text>
        </View>
        {showBetRow ? (
          <View style={styles.matchBetBetCell}>
            <PickCell
              label={opponentPick.label}
              odds={opponentPick.odds}
              isCorrect={opponentPick.isCorrect}
              isWrong={opponentPick.isWrong ?? false}
              containerStyle={pickCellContainerStyle}
              testID={opponentPick.testID}
            />
          </View>
        ) : null}
      </View>
    </View>
  );
};
